import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@packsize/toast";

import { type FilterType, getOrderAudit, getOrdersAuditPreview } from "@/api/walmart-connector-api";
import { type OrderAudit } from "@/schemas";
import { useTenantTimezone } from "@/hooks/use-tenant-timezone";

export const useOrdersAudit = () => {
	const { toastError } = useToast();

	const [filter, setFilter] = useState("");
	const [filterType, setFilterType] = useState<FilterType>("CorrelationId");
	const [modalData, setModalData] = useState<OrderAudit | null>(null);

	const timeZoneData = useTenantTimezone().data?.siteTimeZone ?? "UTC";

	const { isLoading, data, isError, refetch } = useQuery({
		queryFn: getOrdersAuditPreview,
		queryKey: ["orders-audit"],
		staleTime: 60_000,
	});

	const {
		refetch: searchRefetch,
		isLoading: isSearching,
		isFetching: isSearchFetching,
	} = useQuery({
		queryKey: ["orders-audit-search", filterType, filter],
		queryFn: () => getOrderAudit(filterType, filter),
		enabled: false,
	});

	const handleSearch = useCallback(async () => {
		const { data: searchResults, isError: isSearchError } = await searchRefetch();
		if (isSearchError) {
			toastError({ message: "Unexpected Error, please try again" });
			return;
		}

		if (searchResults) {
			setModalData(searchResults);
		} else {
			toastError({ message: "Order not found" });
		}
	}, [searchRefetch, toastError]);

	const onDisplayOrderDetails = useCallback((orderAudit: OrderAudit) => {
		setModalData(orderAudit);
	}, []);
	const onCloseDetailsModal = useCallback(() => {
		setModalData(null);
	}, []);

	return {
		filter,
		setFilter,
		filterType,
		setFilterType,
		data: data ?? [],
		isError,
		refetch,
		handleSearch,
		isLoading,
		modalData,
		onDisplayOrderDetails,
		onCloseDetailsModal,
		isSearching: isSearching || isSearchFetching,
		timeZone: timeZoneData,
	};
};
